/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { IFluidHandle, fluidHandleSymbol } from "@fluidframework/core-interfaces";
/**
 * Is the input object a @see ISerializedHandle?
 * @internal
 */
export const isSerializedHandle = (value) => value?.type === "__fluid_handle__";
/**
 * Setting to opt into compatibility with handles from before {@link fluidHandleSymbol} existed (Fluid Framework client 2.0.0-rc.3.0.0 and earlier).
 *
 * Some code which uses this library might dynamically load multiple versions of it,
 * as well as old or duplicated versions of packages which produce or implement handles.
 * To correctly interoperate with this old packages and object produced by them, the old in-memory format for handles, without the symbol, are explicitly supported.
 *
 * This setting mostly exists as a way to easily find any code that only exists to provide this compatibility and clarify how to remove that compatibility.
 * At some point this might be removed or turned into an actual configuration option, but for now its really just documentation.
 */
const enableBackwardsCompatibility = true;
/**
 * Check if a value is an {@link @fluidframework/core-interfaces#IFluidHandle}.
 * @remarks
 * Objects which have a field named `IFluidHandle` can in some cases produce a false positive.
 * @public
 */
export function isFluidHandle(value) {
    // `in` gives a type error on non-objects and null, so filter them out
    if (typeof value !== "object" || value === null) {
        return false;
    }
    if (fluidHandleSymbol in value) {
        return true;
    }
    // If enableBackwardsCompatibility, run check for FluidHandles predating use of fluidHandleSymbol.
    if (enableBackwardsCompatibility && IFluidHandle in value) {
        // Since this check can have false positives, make it a bit more robust by checking value[IFluidHandle][IFluidHandle]
        const inner = value[IFluidHandle];
        if (typeof inner !== "object" || inner === null) {
            return false;
        }
        return IFluidHandle in inner;
    }
    return false;
}
/**
 * Downcast an IFluidHandle to an IFluidHandleInternal.
 * @legacy
 * @alpha
 */
export function toFluidHandleInternal(handle) {
    if (!(fluidHandleSymbol in handle) || !(fluidHandleSymbol in handle[fluidHandleSymbol])) {
        if (enableBackwardsCompatibility && IFluidHandle in handle) {
            return handle[IFluidHandle];
        }
        throw new TypeError("Invalid IFluidHandle");
    }
    // This casts the IFluidHandleErased from the symbol instead of `handle` to ensure that if someone
    // implements their own IFluidHandle in terms of an existing handle, it won't break anything.
    return handle[fluidHandleSymbol];
}
/**
 * Type erase IFluidHandleInternal for use with {@link @fluidframework/core-interfaces#fluidHandleSymbol}.
 * @legacy
 * @alpha
 */
export function toFluidHandleErased(handle) {
    return handle;
}
/**
 * Base class which can be uses to assist implementing IFluidHandleInternal.
 * @legacy
 * @alpha
 */
export class FluidHandleBase {
    /**
     * {@inheritDoc @fluidframework/core-interfaces#IProvideFluidHandle.IFluidHandle}
     */
    get IFluidHandle() {
        return this;
    }
    get [fluidHandleSymbol]() {
        return toFluidHandleErased(this);
    }
}
//# sourceMappingURL=handles.js.map